import React, { useState, useEffect, useRef } from "react";
import "./form.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ReactGA from "react-ga4";
import emailjs from "@emailjs/browser";

import { MDBInput, MDBCol, MDBRow, MDBBtn } from "mdb-react-ui-kit";

const NOTIFICATION_EMAIL = "info@proptechclub.org";
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const Form = ({ selectedTier = "" }) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [membershipTier, setMembershipTier] = useState(selectedTier);
  const [role, setRole] = useState("");
  const form = useRef();

  useEffect(() => {
    if (selectedTier) {
      setMembershipTier(selectedTier);
    }
  }, [selectedTier]);

  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: "/form-visited" });
  }, []);

  const sendLeadNotification = async () => {
    if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY) {
      console.warn("EmailJS notification is not configured.");
      return null;
    }

    return emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID,
      {
        to_email: NOTIFICATION_EMAIL,
        subject: `New PropTech Club Membership Request - ${membershipTier}`,
        first_name: firstName,
        last_name: lastName,
        from_name: `${firstName} ${lastName}`,
        from_email: email,
        contact_number: contactNumber,
        membership_tier: membershipTier,
        role: role || "Not provided",
        message: [
          `Name: ${firstName} ${lastName}`,
          `Email: ${email}`,
          `Contact No: ${contactNumber}`,
          `Membership Tier: ${membershipTier}`,
          `Role: ${role || "Not provided"}`,
        ].join("\n"),
      },
      EMAILJS_PUBLIC_KEY
    );
  };

  const sendEmail = async (e) => {
    e.preventDefault();

    if (!email || !lastName || !firstName || !contactNumber || !membershipTier) {
      toast.error("Please fill in all required fields.", {
        position: "top-center",
      });
      return;
    }

    const payload = {
      fields: {
        NAME: firstName,
        LAST_NAME: lastName,
        EMAIL: [{ VALUE: email, VALUE_TYPE: "WORK" }],
        PHONE: [{ VALUE: contactNumber, VALUE_TYPE: "WORK" }],
        COMMENTS: `Membership Tier: ${membershipTier}\nRole: ${role || "Not provided"}`,
      },
    };

    try {
      const response = await fetch(
        "https://b24-npfomz.bitrix24.com/rest/13/87zmityoh7f5ea3y/crm.lead.add.json",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();

      if (result.result) {
        try {
          await sendLeadNotification();
        } catch (notificationError) {
          console.error("Email notification error:", notificationError);
        }

        toast.success("Form submitted successfully!", {
          position: "top-center",
        });

        setFirstName("");
        setLastName("");
        setEmail("");
        setContactNumber("");
        setMembershipTier(selectedTier);
        setRole("");
      } else {
        toast.error("Failed to submit form. Please try again.", {
          position: "top-center",
        });
        console.error("Bitrix Error:", result);
      }
    } catch (error) {
      console.error("Submission error:", error);
      toast.error(error.message || "An error occurred. Please try again later.", {
        position: "top-center",
      });
    }
  };

  return (
    <>
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        toastStyle={{
          backgroundColor: "white",
          fontFamily: "Outfit",
          fontSize: "15px",
        }}
        progressStyle={{ background: "#312FB8", opacity: "unset" }}
      />

      <section id="form_section">
        <div id="form_txt_div">
          <h5 id="form_sbhdng">Ready to Supercharge Your Real Estate efforts?</h5>
          <h1 id="form_hdng">Become A Member Today!</h1>

          <p id="form_txt">
            If you&apos;re actively building, investing, or shaping the real estate ecosystem,
            this is where meaningful connections and opportunities begin.
            <br />
            <b>Apply to become part of a curated network of PropTech enablers</b>
          </p>
        </div>

        <div id="form_div">
          <form ref={form} id="form" onSubmit={sendEmail}>
            <MDBRow className="mb-4">
              <MDBCol id="field">
                <MDBInput
                  name="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  id="first_name_input"
                  label="First Name"
                />
              </MDBCol>
              <MDBCol>
                <MDBInput
                  name="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  id="last_name_input"
                  label="Last name"
                />
              </MDBCol>
            </MDBRow>
            <MDBInput
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mb-4"
              type="email"
              id="email_input"
              label="Email address"
              placeholder="123@abc.com"
            />

            <MDBInput
              name="contactNumber"
              value={contactNumber}
              onChange={(e) => setContactNumber(e.target.value.replace(/^(\+92|0)?/, ""))}
              className="mb-4"
              type="text"
              id="phone_input"
              label="Contact No."
              placeholder="321 0000000"
            />

            <label className="form_select_label" htmlFor="membership_tier_select">
              Membership Tier
            </label>
            <select
              id="membership_tier_select"
              className="form_select mb-4"
              value={membershipTier}
              onChange={(e) => setMembershipTier(e.target.value)}
            >
              <option value="">Select a membership tier</option>
              <option value="Founding Member">Founding Member</option>
              <option value="Industry Circle">Industry Circle</option>
              <option value="Strategic Partner">Strategic Partner</option>
            </select>

            <label className="form_select_label" htmlFor="role_select">
              Your Role
            </label>
            <select
              id="role_select"
              className="form_select mb-4"
              value={role}
              onChange={(e) => setRole(e.target.value)}
            >
              <option value="">Select your role</option>
              <option value="Real Estate Developer">Real Estate Developer</option>
              <option value="Investor">Investor</option>
              <option value="Banker / Financial Institution">Banker / Financial Institution</option>
              <option value="Proptech / Technology">Proptech / Technology</option>
              <option value="Broker / Consultant">Broker / Consultant</option>
              <option value="Architect / Designer">Architect / Designer</option>
              <option value="Academia">Academia</option>
            </select>

            <MDBBtn type="submit" className="mb-4 submit_btn" block id="form_btn">
              Submit
            </MDBBtn>
          </form>
        </div>
      </section>
    </>
  );
};

export default Form;
